import React from 'react';

/**
 * Loading placeholders shaped like the content they replace, so the layout
 * doesn't jump when real data arrives. Spinners were the app's only loading
 * state; a lone 8px spinner ahead of a full data table is a big shift.
 *
 * Marked aria-hidden and paired with an sr-only live message at the call site.
 */

export const SkeletonLine: React.FC<{ className?: string }> = ({ className = 'w-full' }) => (
  <span className={`block h-3 rounded bg-surface-sunk animate-pulse ${className}`} aria-hidden="true" />
);

export const SkeletonBlock: React.FC<{ className?: string }> = ({ className = 'h-24' }) => (
  <div className={`rounded-token-md bg-surface-sunk animate-pulse ${className}`} aria-hidden="true" />
);

/** Table body placeholder. Column widths should mirror the real header. */
export const SkeletonRows: React.FC<{ rows?: number; cols?: number }> = ({ rows = 6, cols = 5 }) => (
  <>
    <span className="sr-only" role="status">Loading results</span>
    <div className="divide-y divide-border" aria-hidden="true">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-4">
          {Array.from({ length: cols }).map((_, c) => (
            <SkeletonLine key={c} className={c === 0 ? 'flex-[3]' : 'flex-1'} />
          ))}
        </div>
      ))}
    </div>
  </>
);

/** Card grid placeholder, e.g. dashboard stat tiles. */
export const SkeletonCards: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <>
    <span className="sr-only" role="status">Loading</span>
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBlock key={i} className="h-[88px]" />
      ))}
    </div>
  </>
);
